import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Package, ShoppingCart, Plus, Search, Truck, CheckCircle2, Clock, XCircle,
  RefreshCw, Loader2, ChevronRight, Filter, FileText, Building2, User,
  Calendar, AlertCircle, Phone, MapPin, ArrowRight, Minus, Eye, Send,
  Bed, Wind, Bath, Heart, Activity, Stethoscope
} from "lucide-react";

const ORDER_STATUS = {
  draft: { label: "Draft", color: "bg-gray-100 text-gray-800", icon: FileText },
  pending_approval: { label: "Pending Approval", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  approved: { label: "Approved", color: "bg-blue-100 text-blue-800", icon: CheckCircle2 },
  submitted: { label: "Submitted", color: "bg-indigo-100 text-indigo-800", icon: Send },
  processing: { label: "Processing", color: "bg-purple-100 text-purple-800", icon: Activity },
  shipped: { label: "Shipped", color: "bg-cyan-100 text-cyan-800", icon: Truck },
  delivered: { label: "Delivered", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800", icon: XCircle }
};

const CATEGORY_ICONS = {
  hospital_bed: Bed,
  wheelchair: User,  // Wheelchair icon not available, using User as fallback
  oxygen: Wind,
  walker: User,
  commode: Bath,
  suction: Activity,
  nebulizer: Wind,
  mattress: Bed,
  lift: User,
  supplies: Package
};

const PRIORITY_CONFIG = {
  urgent: { label: "Urgent", color: "bg-red-500 text-white" },
  high: { label: "High", color: "bg-orange-500 text-white" },
  normal: { label: "Normal", color: "bg-blue-500 text-white" }
};

export default function DMESuppliesOrdering() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("catalog");
  
  // Catalog
  const [catalog, setCatalog] = useState([]);
  const [categories, setCategories] = useState({ equipment: [], supplies: [] });
  const [vendors, setVendors] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Cart
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  
  // Orders
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  
  // New Order
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [newOrder, setNewOrder] = useState({
    patient_id: "",
    patient_name: "",
    patient_address: "",
    patient_phone: "",
    vendor_id: "",
    priority: "normal",
    delivery_date_requested: "",
    delivery_instructions: "",
    physician_name: "",
    physician_npi: "",
    diagnosis_codes: "",
    notes: ""
  });
  
  const [submitting, setSubmitting] = useState(false);

  const allowedRoles = ["admin", "administrator", "staff", "nurse", "coordinator", "social_worker"];

  useEffect(() => {
    if (authLoading) return;
    if (!user || !allowedRoles.includes(user?.role?.toLowerCase())) {
      toast.error("Access denied");
      navigate("/");
      return;
    }
    loadData();
  }, [user, authLoading, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCatalog(),
        loadCategories(),
        loadVendors(),
        loadOrders()
      ]);
    } catch (error) {
      console.error("Load error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCatalog = async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      if (searchTerm) params.append("search", searchTerm);
      
      const response = await api.get(`/dme-supplies/catalog?${params.toString()}`);
      setCatalog(response.data.items || []);
    } catch (error) {
      console.error("Failed to load catalog:", error);
      setCatalog([]);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get("/dme-supplies/categories");
      setCategories({
        equipment: response.data.equipment_categories || [],
        supplies: response.data.supply_categories || []
      });
    } catch (error) {
      console.error("Failed to load categories:", error);
    }
  };

  const loadVendors = async () => {
    try {
      const response = await api.get("/dme-supplies/vendors");
      setVendors(response.data.vendors || []);
    } catch (error) {
      console.error("Failed to load vendors:", error);
      setVendors([]);
    }
  };

  const loadOrders = async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await api.get(`/dme-supplies/orders${params}`);
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error("Failed to load orders:", error);
      setOrders([]);
    }
  };

  useEffect(() => {
    if (!loading) loadCatalog();
  }, [categoryFilter, searchTerm]);

  useEffect(() => {
    if (!loading) loadOrders();
  }, [statusFilter]);

  const addToCart = (item) => {
    const existing = cart.find(c => c.item_id === item.id || c.item_id === item.item_id);
    if (existing) {
      setCart(cart.map(c => 
        (c.item_id === item.id || c.item_id === item.item_id) 
          ? { ...c, quantity: c.quantity + 1 } 
          : c
      ));
    } else {
      setCart([...cart, {
        item_id: item.id || item.item_id || `item-${Date.now()}`,
        item_name: item.name,
        hcpcs_code: item.hcpcs_code,
        quantity: 1,
        unit_price: item.unit_price || null,
        is_rental: item.is_rental || false,
        notes: ""
      }]);
    }
    toast.success(`Added ${item.name} to cart`);
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(c => c.item_id !== itemId));
  };

  const updateCartQuantity = (itemId, quantity) => {
    if (quantity < 1) {
      removeFromCart(itemId);
      return;
    }
    setCart(cart.map(c => c.item_id === itemId ? { ...c, quantity } : c));
  };

  const handleCreateOrder = async () => {
    if (!newOrder.patient_name || cart.length === 0) {
      toast.error("Patient name and at least one item are required");
      return;
    }

    setSubmitting(true);
    try {
      const orderData = {
        ...newOrder,
        items: cart,
        diagnosis_codes: newOrder.diagnosis_codes ? newOrder.diagnosis_codes.split(",").map(c => c.trim()) : []
      };
      
      const response = await api.post("/dme-supplies/orders", orderData);
      toast.success(`Order ${response.data.order_number} created`);
      setShowNewOrder(false);
      setCart([]);
      setNewOrder({
        patient_id: "", patient_name: "", patient_address: "", patient_phone: "",
        vendor_id: "", priority: "normal", delivery_date_requested: "",
        delivery_instructions: "", physician_name: "", physician_npi: "",
        diagnosis_codes: "", notes: ""
      });
      loadOrders();
      setActiveTab("orders");
    } catch (error) {
      toast.error("Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitOrder = async (orderId) => {
    try {
      await api.post(`/dme-supplies/orders/${orderId}/submit`);
      toast.success("Order submitted to vendor");
      loadOrders();
      setShowOrderDetail(false);
    } catch (error) {
      toast.error("Failed to submit order");
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      await api.delete(`/dme-supplies/orders/${orderId}?reason=Cancelled by user`);
      toast.success("Order cancelled");
      loadOrders();
      setShowOrderDetail(false);
    } catch (error) {
      toast.error("Failed to cancel order");
    }
  };

  const viewOrderDetail = async (orderId) => {
    try {
      const response = await api.get(`/dme-supplies/orders/${orderId}`);
      setSelectedOrder(response.data);
      setShowOrderDetail(true);
    } catch (error) {
      toast.error("Failed to load order details");
    }
  };

  const getCategoryIcon = (category) => {
    const Icon = CATEGORY_ICONS[category] || Package;
    return <Icon className="h-5 w-5" />;
  };

  const cartTotal = cart.reduce((sum, item) => sum + ((item.unit_price || 0) * item.quantity), 0);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center" data-testid="dme-loading">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-teal-400 mb-4" />
          <p className="text-slate-400">Loading DME & Supplies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="dme-supplies-ordering">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Package className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">DME & Medical Supplies</h1>
                <p className="text-teal-100">Order equipment and supplies for patients</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10 relative"
                onClick={() => setShowCart(true)}
                data-testid="cart-btn"
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                Cart
                {cart.length > 0 && (
                  <Badge className="absolute -top-2 -right-2 bg-red-500">{cart.length}</Badge>
                )}
              </Button>
              <Button onClick={loadData} variant="outline" className="border-white/30 text-white hover:bg-white/10">
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button 
                onClick={() => setShowNewOrder(true)} 
                className="bg-white text-teal-600 hover:bg-teal-50"
                disabled={cart.length === 0}
                data-testid="create-order-btn"
              >
                <Plus className="h-4 w-4 mr-2" />Create Order
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="catalog" className="data-[state=active]:bg-teal-600 text-white">
              <Package className="h-4 w-4 mr-2" />Catalog
            </TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-teal-600 text-white">
              <FileText className="h-4 w-4 mr-2" />Orders
              {orders.filter(o => o.status === "pending_approval").length > 0 && (
                <Badge className="ml-2 bg-yellow-500">{orders.filter(o => o.status === "pending_approval").length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="vendors" className="data-[state=active]:bg-teal-600 text-white">
              <Building2 className="h-4 w-4 mr-2" />Vendors
            </TabsTrigger>
          </TabsList>

          {/* Catalog Tab */}
          <TabsContent value="catalog" className="space-y-4" data-testid="catalog-tab">
            {/* Filters */}
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search by name or HCPCS code..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 bg-slate-700 border-slate-600 text-white"
                        data-testid="catalog-search"
                      />
                    </div>
                  </div>
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-[180px] bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.equipment.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                      {categories.supplies.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Category Quick Filters */}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={categoryFilter === "all" ? "default" : "outline"}
                className={categoryFilter === "all" ? "bg-teal-600" : "border-slate-600 text-slate-300"}
                onClick={() => setCategoryFilter("all")}
              >
                All
              </Button>
              {categories.equipment.slice(0, 6).map((cat) => (
                <Button
                  key={cat.id}
                  size="sm"
                  variant={categoryFilter === cat.id ? "default" : "outline"}
                  className={categoryFilter === cat.id ? "bg-teal-600" : "border-slate-600 text-slate-300"}
                  onClick={() => setCategoryFilter(cat.id)}
                >
                  {getCategoryIcon(cat.id)}
                  <span className="ml-2">{cat.name}</span>
                </Button>
              ))}
            </div>

            {/* Catalog Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {catalog.map((item, idx) => (
                <Card key={item.id || idx} className="bg-slate-800 border-slate-700 hover:border-teal-500 transition-colors" data-testid={`catalog-item-${idx}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-teal-500/20 rounded-lg">
                          {getCategoryIcon(item.category)}
                        </div>
                        <div>
                          <p className="font-medium text-white">{item.name}</p>
                          {item.hcpcs_code && (
                            <Badge variant="outline" className="mt-1 border-slate-600 text-slate-400 text-xs">
                              HCPCS: {item.hcpcs_code}
                            </Badge>
                          )}
                        </div>
                      </div>
                      {item.is_rental && (
                        <Badge className="bg-purple-500/20 text-purple-300">Rental</Badge>
                      )}
                    </div>
                    
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {item.requires_prescription && (
                          <Badge variant="outline" className="border-yellow-500/50 text-yellow-400 text-xs">Rx Required</Badge>
                        )}
                        {item.requires_prior_auth && (
                          <Badge variant="outline" className="border-orange-500/50 text-orange-400 text-xs">Prior Auth</Badge>
                        )}
                      </div>
                      <Button 
                        size="sm" 
                        className="bg-teal-600 hover:bg-teal-700"
                        onClick={() => addToCart(item)}
                      >
                        <Plus className="h-4 w-4 mr-1" />Add
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {catalog.length === 0 && (
                <div className="col-span-full text-center py-12 text-slate-400">
                  <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No items found</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-4" data-testid="orders-tab">
            {/* Filters */}
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[180px] bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      {Object.entries(ORDER_STATUS).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" className="border-slate-600 text-slate-300" onClick={loadOrders}>
                    <RefreshCw className="h-4 w-4 mr-2" />Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Orders List */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Orders ({orders.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  {orders.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No orders found</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {orders.map((order) => {
                        const statusConfig = ORDER_STATUS[order.status] || ORDER_STATUS.draft;
                        const StatusIcon = statusConfig.icon;
                        return (
                          <div
                            key={order.id}
                            className="p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 cursor-pointer transition-colors"
                            onClick={() => viewOrderDetail(order.id)}
                            data-testid={`order-${order.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="p-2 bg-teal-500/20 rounded-lg">
                                  <StatusIcon className="h-5 w-5 text-teal-400" />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-semibold text-white">{order.order_number}</p>
                                    <Badge className={PRIORITY_CONFIG[order.priority]?.color || "bg-blue-500"}>{order.priority}</Badge>
                                  </div>
                                  <p className="text-sm text-slate-400">{order.patient_name}</p>
                                  <p className="text-xs text-slate-500">{order.items?.length || 0} items • {order.vendor_name || "No vendor"}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                                  <p className="text-xs text-slate-500 mt-1">
                                    {new Date(order.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                                <ChevronRight className="h-5 w-5 text-slate-400" />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vendors Tab */}
          <TabsContent value="vendors" className="space-y-4" data-testid="vendors-tab">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {vendors.map((vendor, idx) => (
                <Card key={vendor.id || idx} className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-cyan-500/20 rounded-lg">
                        <Building2 className="h-5 w-5 text-cyan-400" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-white">{vendor.name}</p>
                        {vendor.code && <p className="text-xs text-slate-500">Code: {vendor.code}</p>}
                        
                        <div className="mt-3 space-y-1 text-sm text-slate-400">
                          {vendor.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-3 w-3" />{vendor.phone}
                            </div>
                          )}
                          {vendor.email && (
                            <div className="flex items-center gap-2 truncate">
                              <span>@</span>{vendor.email}
                            </div>
                          )}
                        </div>
                        
                        {vendor.categories && (
                          <div className="mt-3 flex flex-wrap gap-1">
                            {vendor.categories.slice(0, 4).map((cat) => (
                              <Badge key={cat} variant="outline" className="text-xs border-slate-600 text-slate-400">
                                {cat}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {vendors.length === 0 && (
                <div className="col-span-full text-center py-12 text-slate-400">
                  <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No vendors configured</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Cart Dialog */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-teal-400" />
              Cart ({cart.length} items)
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[400px]">
            {cart.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <ShoppingCart className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Your cart is empty</p>
              </div>
            ) : (
              <div className="space-y-3">
                {cart.map((item) => (
                  <div key={item.item_id} className="p-3 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-white">{item.item_name}</p>
                        {item.hcpcs_code && <p className="text-xs text-slate-400">HCPCS: {item.hcpcs_code}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" className="h-8 w-8 p-0 border-slate-600" onClick={() => updateCartQuantity(item.item_id, item.quantity - 1)}>
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-8 text-center">{item.quantity}</span>
                        <Button size="sm" variant="outline" className="h-8 w-8 p-0 border-slate-600" onClick={() => updateCartQuantity(item.item_id, item.quantity + 1)}>
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400 hover:text-red-300" onClick={() => removeFromCart(item.item_id)}>
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          <DialogFooter className="flex-col gap-2">
            <Button 
              className="w-full bg-teal-600 hover:bg-teal-700" 
              disabled={cart.length === 0}
              onClick={() => { setShowCart(false); setShowNewOrder(true); }}
            >
              <ArrowRight className="h-4 w-4 mr-2" />Continue to Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Order Dialog */}
      <Dialog open={showNewOrder} onOpenChange={setShowNewOrder}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Create DME/Supplies Order</DialogTitle>
            <DialogDescription className="text-slate-400">Enter patient and delivery information</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Patient Info */}
            <div className="p-4 bg-slate-700/50 rounded-lg">
              <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                <User className="h-4 w-4 text-teal-400" />Patient Information
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Patient Name *</Label>
                  <Input
                    value={newOrder.patient_name}
                    onChange={(e) => setNewOrder({ ...newOrder, patient_name: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                    placeholder="Full name"
                    data-testid="order-patient-name"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Delivery Address</Label>
                  <Input
                    value={newOrder.patient_address}
                    onChange={(e) => setNewOrder({ ...newOrder, patient_address: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                    placeholder="Street address, city, state, zip"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={newOrder.patient_phone}
                    onChange={(e) => setNewOrder({ ...newOrder, patient_phone: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label>Physician Name</Label>
                  <Input
                    value={newOrder.physician_name}
                    onChange={(e) => setNewOrder({ ...newOrder, physician_name: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>
            </div>

            {/* Order Details */}
            <div className="p-4 bg-slate-700/50 rounded-lg">
              <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                <Package className="h-4 w-4 text-teal-400" />Order Details
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Vendor</Label>
                  <Select value={newOrder.vendor_id} onValueChange={(v) => setNewOrder({ ...newOrder, vendor_id: v })}>
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue placeholder="Select vendor..." />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map((v) => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={newOrder.priority} onValueChange={(v) => setNewOrder({ ...newOrder, priority: v })}>
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Requested Delivery Date</Label>
                  <Input
                    type="date"
                    value={newOrder.delivery_date_requested}
                    onChange={(e) => setNewOrder({ ...newOrder, delivery_date_requested: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label>Diagnosis Codes</Label>
                  <Input
                    value={newOrder.diagnosis_codes}
                    onChange={(e) => setNewOrder({ ...newOrder, diagnosis_codes: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                    placeholder="I50.9, J44.9"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Delivery Instructions</Label>
                  <Textarea
                    value={newOrder.delivery_instructions}
                    onChange={(e) => setNewOrder({ ...newOrder, delivery_instructions: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                    placeholder="Special delivery instructions..."
                  />
                </div>
              </div>
            </div>

            {/* Items Summary */}
            <div className="p-4 bg-slate-700/50 rounded-lg">
              <h4 className="font-medium text-white mb-3">Items ({cart.length})</h4>
              <div className="space-y-2">
                {cart.map((item) => (
                  <div key={item.item_id} className="flex items-center justify-between text-sm">
                    <span className="text-slate-300">{item.item_name}</span>
                    <span className="text-slate-400">x{item.quantity}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="border-slate-600" onClick={() => setShowNewOrder(false)}>Cancel</Button>
            <Button onClick={handleCreateOrder} disabled={submitting} className="bg-teal-600 hover:bg-teal-700" data-testid="submit-order-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Create Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Detail Dialog */}
      <Dialog open={showOrderDetail} onOpenChange={setShowOrderDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700 text-white">
          {selectedOrder && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  Order {selectedOrder.order_number}
                  <Badge className={ORDER_STATUS[selectedOrder.status]?.color}>{ORDER_STATUS[selectedOrder.status]?.label}</Badge>
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  Created {new Date(selectedOrder.created_at).toLocaleString()}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-400">Patient</p>
                    <p className="text-white font-medium">{selectedOrder.patient_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Priority</p>
                    <Badge className={PRIORITY_CONFIG[selectedOrder.priority]?.color}>{selectedOrder.priority}</Badge>
                  </div>
                  {selectedOrder.vendor_name && (
                    <div>
                      <p className="text-sm text-slate-400">Vendor</p>
                      <p className="text-white">{selectedOrder.vendor_name}</p>
                    </div>
                  )}
                  {selectedOrder.delivery_date_requested && (
                    <div>
                      <p className="text-sm text-slate-400">Requested Delivery</p>
                      <p className="text-white">{selectedOrder.delivery_date_requested}</p>
                    </div>
                  )}
                </div>

                <Separator className="bg-slate-700" />

                <div>
                  <h4 className="font-medium text-white mb-3">Items</h4>
                  <div className="space-y-2">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                        <div>
                          <p className="text-white">{item.item_name}</p>
                          {item.hcpcs_code && <p className="text-xs text-slate-400">HCPCS: {item.hcpcs_code}</p>}
                        </div>
                        <div className="flex items-center gap-4">
                          {item.is_rental && <Badge className="bg-purple-500/20 text-purple-300 text-xs">Rental</Badge>}
                          <span className="text-slate-300">x{item.quantity}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Status History */}
                {selectedOrder.status_history?.length > 0 && (
                  <>
                    <Separator className="bg-slate-700" />
                    <div>
                      <h4 className="font-medium text-white mb-3">Status History</h4>
                      <div className="space-y-2">
                        {selectedOrder.status_history.map((entry, idx) => (
                          <div key={idx} className="flex items-center gap-3 text-sm">
                            <div className={`w-2 h-2 rounded-full ${ORDER_STATUS[entry.status]?.color?.replace('text-', 'bg-').split(' ')[0] || 'bg-gray-500'}`} />
                            <span className="text-slate-300">{ORDER_STATUS[entry.status]?.label || entry.status}</span>
                            <span className="text-slate-500">{new Date(entry.timestamp).toLocaleString()}</span>
                            {entry.user_name && <span className="text-slate-500">by {entry.user_name}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <DialogFooter>
                {selectedOrder.status === "draft" && (
                  <>
                    <Button variant="outline" className="border-red-500/50 text-red-400" onClick={() => handleCancelOrder(selectedOrder.id)}>Cancel Order</Button>
                    <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => handleSubmitOrder(selectedOrder.id)}>
                      <Send className="h-4 w-4 mr-2" />Submit to Vendor
                    </Button>
                  </>
                )}
                {selectedOrder.status === "pending_approval" && (
                  <Button variant="outline" className="border-red-500/50 text-red-400" onClick={() => handleCancelOrder(selectedOrder.id)}>Cancel Order</Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
